import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Search, Loader2, User, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import PostureImageUpload from '../../components/PostureImageUpload';
import MeasurementCategoryCard from '../../components/MeasurementCategoryCard';

const newKey = (cat) => `${cat}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const SCORE_FIELD_MAP = {
  strength: 'strength_score',
  muscular_endurance: 'endurance_score',
  cardio: 'cardio_score',
  inbody: 'body_composition_score',
  posture: 'body_balance_score',
};

export default function FitReportCreate() {
  const navigate = useNavigate();

  // Step 1: member search
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Step 2: form
  const [selectedMember, setSelectedMember] = useState(null);
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [memberSnapshot, setMemberSnapshot] = useState(null);
  const [loadingSource, setLoadingSource] = useState(false);

  // V2: Measurement rows per category
  const [strengthRows, setStrengthRows] = useState([]);
  const [enduranceRows, setEnduranceRows] = useState([]);
  const [cardioRows, setCardioRows] = useState([]);

  // Inbody (4 direct fields)
  const [inbodyValues, setInbodyValues] = useState({
    weight_kg: '', skeletal_muscle_kg: '', body_fat_pct: '', inbody_score: '',
  });

  // Posture
  const [postureScore, setPostureScore] = useState('');
  const [postureImage, setPostureImage] = useState(null);
  const [postureComment, setPostureComment] = useState('');
  const [postureImprovement, setPostureImprovement] = useState('');

  const [notes, setNotes] = useState('');
  const [saveToMeasurements, setSaveToMeasurements] = useState(true);
  const [saving, setSaving] = useState(false);

  // --- Member search ---
  useEffect(() => {
    if (selectedMember) return;
    if (!searchQ || searchQ.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiClient.get('/admin/members/search', { params: { q: searchQ.trim() } });
        setSearchResults(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ, selectedMember]);

  const handleSelectMember = async (m) => {
    setSelectedMember(m);
    setSearchResults([]);
    setSearchQ('');
    setLoadingSource(true);
    try {
      const res = await apiClient.get(`/admin/members/${m._id}/report-source`);
      setMemberSnapshot(res.data.member_snapshot);

      // Pre-fill inbody from latest snapshot (if any)
      const inb = res.data.inbody_snapshot || {};
      setInbodyValues({
        weight_kg: inb.weight ?? '',
        skeletal_muscle_kg: inb.skeletal_muscle_mass ?? '',
        body_fat_pct: inb.body_fat_percentage ?? '',
        inbody_score: inb.inbody_score ?? '',
      });
    } catch (e) {
      console.error(e);
      toast.error('회원 데이터를 불러오지 못했습니다');
      setSelectedMember(null);
    } finally {
      setLoadingSource(false);
    }
  };

  // --- Area averages (with drop-lowest if 5) ---
  const areaScore = (rows) => {
    const arr = rows.map((r) => r.score).filter((s) => s !== null && s !== undefined && !Number.isNaN(s));
    if (arr.length === 0) return null;
    const trimmed = arr.length >= 5 ? [...arr].sort((a, b) => a - b).slice(1) : arr;
    return Math.round((trimmed.reduce((s, v) => s + v, 0) / trimmed.length) * 10) / 10;
  };

  const strengthAvg = useMemo(() => areaScore(strengthRows), [strengthRows]);
  const enduranceAvg = useMemo(() => areaScore(enduranceRows), [enduranceRows]);
  const cardioAvg = useMemo(() => areaScore(cardioRows), [cardioRows]);

  const inbodyScoreVal = inbodyValues.inbody_score === '' || inbodyValues.inbody_score === null
    ? null
    : Number(inbodyValues.inbody_score);
  const postureScoreVal = postureScore === '' || postureScore === null ? null : Number(postureScore);

  const totalScore = useMemo(() => {
    const arr = [strengthAvg, enduranceAvg, cardioAvg, inbodyScoreVal, postureScoreVal]
      .filter((v) => v !== null && !Number.isNaN(v));
    if (!arr.length) return null;
    return Math.round((arr.reduce((s, v) => s + v, 0) / arr.length) * 10) / 10;
  }, [strengthAvg, enduranceAvg, cardioAvg, inbodyScoreVal, postureScoreVal]);

  // --- Submit ---
  const handleSubmit = async () => {
    if (!selectedMember) {
      toast.error('회원을 선택해주세요');
      return;
    }
    setSaving(true);
    try {
      // Build measurement_inputs from all category rows + inbody + posture
      const measurement_inputs = [];

      const collect = (cat, rowList) => {
        rowList.forEach((r) => {
          // Filter only rows with at least one numeric value
          const hasVal = Object.values(r.raw_metrics || {}).some((v) => v !== null && v !== undefined && v !== '');
          if (!hasVal) return;
          // Cast numbers
          const rm = {};
          Object.entries(r.raw_metrics).forEach(([k, v]) => {
            const n = Number(v);
            rm[k] = Number.isNaN(n) ? v : n;
          });
          measurement_inputs.push({ category: cat, exercise: r.exercise, raw_metrics: rm });
        });
      };
      collect('strength', strengthRows);
      collect('muscular_endurance', enduranceRows);
      collect('cardio', cardioRows);

      // Inbody (only if any value entered)
      const inbAny = Object.values(inbodyValues).some((v) => v !== null && v !== undefined && v !== '');
      if (inbAny) {
        const inbRaw = {};
        Object.entries(inbodyValues).forEach(([k, v]) => {
          if (v === '' || v === null) return;
          const n = Number(v);
          inbRaw[k] = Number.isNaN(n) ? v : n;
        });
        measurement_inputs.push({ category: 'inbody', exercise: 'inbody', raw_metrics: inbRaw });
      }

      // Posture (if score entered)
      if (postureScore !== '' && postureScore !== null) {
        measurement_inputs.push({
          category: 'posture',
          exercise: 'posture',
          raw_metrics: { posture_score: Number(postureScore) },
        });
      }

      const payload = {
        member_id: selectedMember._id,
        report_date: reportDate,
        status: 'draft',
        member_snapshot: memberSnapshot,
        measurement_inputs,
        save_to_measurements: saveToMeasurements,
        posture_snapshot: {
          image_data_url: postureImage,
          comment: postureComment,
          improvement_points: postureImprovement,
          posture_score: postureScoreVal,
        },
        notes,
      };
      const res = await apiClient.post('/admin/fit-reports', payload);
      const saved = res.data?.saved_measurement_ids?.length || 0;
      toast.success(
        `FIT REPORT가 생성되었습니다${saved ? ` (회원 측정 ${saved}건 반영)` : ''}`
      );
      navigate(`/admin/fit-reports/${res.data.report_id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || '리포트 생성에 실패했습니다');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto" data-testid="fit-report-create">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/fit-reports')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> 목록
        </Button>
        <h1 className="text-2xl font-semibold text-gray-900">FIT REPORT 생성</h1>
      </div>

      {/* Step 1: Member search */}
      {!selectedMember && (
        <Card>
          <CardContent className="pt-5 pb-5 space-y-3">
            <Label className="text-base font-medium text-gray-900">1. 회원 검색 및 선택</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="회원명 또는 전화번호 (2자 이상)"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="pl-9"
                data-testid="input-member-search"
                autoFocus
              />
            </div>
            {searching && (
              <div className="text-sm text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> 검색 중...
              </div>
            )}
            {!searching && searchResults.length > 0 && (
              <div className="border rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
                {searchResults.map((m) => (
                  <button
                    key={m._id}
                    onClick={() => handleSelectMember(m)}
                    data-testid={`member-option-${m._id}`}
                    className="w-full text-left px-4 py-3 hover:bg-orange-50/60 transition-colors flex items-center gap-3"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{m.name}</div>
                      <div className="text-xs text-gray-500">{m.phone || '-'} · {m.member_number || '-'}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!searching && searchQ.trim().length >= 2 && searchResults.length === 0 && (
              <div className="text-sm text-gray-400">검색 결과가 없습니다</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2+: Selected member */}
      {selectedMember && (
        <>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm text-gray-500">회원 정보</div>
                  {loadingSource ? (
                    <div className="mt-1 text-gray-400 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> 데이터 불러오는 중...
                    </div>
                  ) : memberSnapshot ? (
                    <div className="mt-1 space-y-0.5">
                      <div className="text-lg font-semibold text-gray-900">{memberSnapshot.name}</div>
                      <div className="text-sm text-gray-600">
                        {memberSnapshot.gender === 'male' ? '남' : memberSnapshot.gender === 'female' ? '여' : '-'} · {memberSnapshot.age ?? '-'}세 · {memberSnapshot.phone || '-'}
                      </div>
                    </div>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedMember(null);
                    setMemberSnapshot(null);
                    setStrengthRows([]); setEnduranceRows([]); setCardioRows([]);
                    setInbodyValues({ weight_kg: '', skeletal_muscle_kg: '', body_fat_pct: '', inbody_score: '' });
                    setPostureScore(''); setPostureImage(null); setPostureComment(''); setPostureImprovement('');
                  }}
                >
                  회원 변경
                </Button>
              </div>
            </CardContent>
          </Card>

          {!loadingSource && memberSnapshot && (
            <>
              {/* Report date */}
              <Card>
                <CardContent className="pt-5 pb-5 space-y-2">
                  <Label htmlFor="report-date" className="text-base font-medium text-gray-900">Report Date</Label>
                  <p className="text-xs text-gray-500">측정일이 아닌, 리포트 제공일/생성일입니다.</p>
                  <Input
                    id="report-date"
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    className="max-w-xs"
                    data-testid="input-report-date"
                  />
                </CardContent>
              </Card>

              {/* Info banner */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  실제 측정 데이터를 입력하시면 기존 점수 계산 로직(`calculate_measurement_score`)을 통해 점수가 자동 계산됩니다.
                  영역 점수는 입력 행의 평균(5건 이상이면 최저 1건 제외)으로 산출됩니다.
                </div>
              </div>

              {/* Measurement cards: strength / muscular_endurance / cardio */}
              <MeasurementCategoryCard
                category="strength"
                memberId={selectedMember._id}
                rows={strengthRows}
                onChange={setStrengthRows}
              />
              <MeasurementCategoryCard
                category="muscular_endurance"
                memberId={selectedMember._id}
                rows={enduranceRows}
                onChange={setEnduranceRows}
              />
              <MeasurementCategoryCard
                category="cardio"
                memberId={selectedMember._id}
                rows={cardioRows}
                onChange={setCardioRows}
              />

              {/* Inbody (직접 입력) */}
              <Card>
                <CardContent className="pt-5 pb-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium text-gray-900">신체조성 (인바디)</Label>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">영역 점수</div>
                      <div className="text-2xl font-bold text-orange-500">
                        {inbodyScoreVal ?? '-'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs text-gray-600">체중 (kg)</Label>
                      <Input
                        type="number" step="0.1"
                        value={inbodyValues.weight_kg}
                        onChange={(e) => setInbodyValues((p) => ({ ...p, weight_kg: e.target.value }))}
                        data-testid="input-inbody-weight"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">골격근량 (kg)</Label>
                      <Input
                        type="number" step="0.1"
                        value={inbodyValues.skeletal_muscle_kg}
                        onChange={(e) => setInbodyValues((p) => ({ ...p, skeletal_muscle_kg: e.target.value }))}
                        data-testid="input-inbody-smm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">체지방률 (%)</Label>
                      <Input
                        type="number" step="0.1"
                        value={inbodyValues.body_fat_pct}
                        onChange={(e) => setInbodyValues((p) => ({ ...p, body_fat_pct: e.target.value }))}
                        data-testid="input-inbody-bfp"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">인바디 점수</Label>
                      <Input
                        type="number" step="0.1"
                        value={inbodyValues.inbody_score}
                        onChange={(e) => setInbodyValues((p) => ({ ...p, inbody_score: e.target.value }))}
                        data-testid="input-inbody-score"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Posture (신체균형 / 체형) */}
              <Card>
                <CardContent className="pt-5 pb-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium text-gray-900">신체균형 (체형)</Label>
                      <p className="text-xs text-gray-500 mt-0.5">자세 점수(0~100)는 영역 점수에 그대로 반영됩니다.</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">영역 점수</div>
                      <div className="text-2xl font-bold text-orange-500">{postureScoreVal ?? '-'}</div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-gray-600">자세 점수 (0~100)</Label>
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      value={postureScore}
                      onChange={(e) => setPostureScore(e.target.value)}
                      className="max-w-xs"
                      data-testid="input-posture-score"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-600">체형 이미지</Label>
                    <PostureImageUpload value={postureImage} onChange={setPostureImage} />
                  </div>

                  <div>
                    <Label className="text-xs text-gray-600">체형 코멘트</Label>
                    <Textarea
                      rows={3}
                      value={postureComment}
                      onChange={(e) => setPostureComment(e.target.value)}
                      placeholder='예) 우측 어깨 상승 및 전방머리 자세가 관찰됩니다.'
                      data-testid="input-posture-comment"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-gray-600">개선 포인트</Label>
                    <Textarea
                      rows={3}
                      value={postureImprovement}
                      onChange={(e) => setPostureImprovement(e.target.value)}
                      placeholder='예) 흉추 가동성 향상 및 견갑 안정화 운동 필요'
                      data-testid="input-posture-improvement"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Total score preview */}
              <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs tracking-widest text-orange-700 font-semibold">TOTAL SCORE</div>
                      <div className="text-xs text-gray-600 mt-1">5개 영역 점수의 평균</div>
                    </div>
                    <div className="text-5xl font-bold text-orange-500" data-testid="preview-total-score">
                      {totalScore ?? '-'}
                      <span className="text-xl text-gray-400 ml-1">/100</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardContent className="pt-5 pb-5 space-y-2">
                  <Label className="text-base font-medium text-gray-900">상담 메모 (선택)</Label>
                  <Textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="리포트 화면에 함께 표시할 메모"
                    data-testid="input-notes"
                  />
                </CardContent>
              </Card>

              {/* Save options */}
              <Card>
                <CardContent className="pt-5 pb-5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={saveToMeasurements}
                      onCheckedChange={(c) => setSaveToMeasurements(c === true)}
                      className="mt-0.5"
                      data-testid="checkbox-save-to-measurements"
                    />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">이 측정 기록을 회원 점수에도 반영</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        체크 시 입력한 측정값이 회원의 측정 이력(measurements)에도 저장되어 회원 점수에 반영됩니다.
                        체크 해제 시 이 리포트의 스냅샷에만 박제됩니다.
                      </div>
                    </div>
                  </label>
                </CardContent>
              </Card>

              {/* Save */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => navigate('/admin/fit-reports')}>취소</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="btn-save-report"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  생성 및 저장
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
